import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Redirect, useLocation } from 'react-router-dom';
import { Button, Center, Divider, Spinner } from 'src/components/atoms';
import { StyledIcon } from 'src/components/foundations/foundations.styles';
import {
  ColumnType,
  DropdownSelect,
  EmptyState,
  handleErrorToast,
  HeroMetric,
  Modal,
  Pagination,
  ProgressCard,
  StatusBadge,
  StatusBadgeStatus,
} from 'src/components/molecules';
import { SlideOut } from 'src/components/molecules/SlideOut/SlideOut';
import { PaginatedTable } from 'src/components/organisms/PaginatedTable';
import {
  CaseImporterJobHistoryItem,
  useGetCaseImporterJobHistoryQuery,
  useGetCaseImporterJobProgressQuery,
  useStopCaseImporterJobMutation,
} from 'src/graphql';
import { getReadableId } from 'src/types';
import {
  trackCaseImportHistoryReportOpenedEvent,
  trackCaseImportHistoryViewedEvent,
} from 'src/utils/analytics/track/caseImport';
import { DateFormats, formatDate } from 'src/utils/time';
import { ImportReportDetails } from './ImportReportDetails';
import {
  Container,
  FailedCasesText,
  FooterContainer,
  ImportReportContent,
  ImportReportDividerWrapper,
  ImportReportHeader,
  ImportReportMetricCard,
  ImportReportMetricsContainer,
  ModalDescription,
  PaginationContainer,
  RightContainer,
  ShowingText,
  StatusDetailLine,
  StatusDetailsContainer,
  TableWrapper,
  Top,
  UploadHistoryContainer,
} from './UploadCasesHistory.styles';
import { uploadCasesHistoryVM } from './UploadCasesHistory.vm';

const formatImportDate = (date: string | Date): string => {
  return formatDate(new Date(date), DateFormats.DAY_MONTH_YEAR_TIME, false);
};

const getStatusBadgeStatus = (
  status: string,
  successfulCases: number,
  failedCases: number,
  totalCases: number,
): StatusBadgeStatus => {
  if (status === 'FAILED' || failedCases === totalCases) {
    return 'FAILED';
  }
  if (status === 'COMPLETED') {
    if (failedCases > 0 && successfulCases < totalCases) {
      return 'PARTIAL_SUCCESS';
    }
    return 'COMPLETED';
  }
  return 'COMPLETED';
};

export const UploadCasesHistory = () => {
  const [redirectUrl, setRedirectUrl] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [isReportOpen, setIsReportOpen] = useState(false);
  const [stopImportModalOpen, setStopImportModalOpen] = useState(false);
  const [stopImportLoading, setStopImportLoading] = useState(false);

  const location = useLocation<{ fromNavbar?: boolean }>();
  const { organisationId, segmentEnabled, userId, userSystemRole } =
    useSelector(uploadCasesHistoryVM);

  const { data: activeJobData, isLoading: isJobProgressLoading } =
    useGetCaseImporterJobProgressQuery(
      {},
      {
        refetchInterval: (data) => {
          const status = data?.getCaseImporterJobProgress?.status;
          if (
            status === 'COMPLETED' ||
            status === 'FAILED' ||
            status === 'CANCELLED'
          ) {
            return false;
          }
          return 1000;
        },
        refetchIntervalInBackground: true,
      },
    );

  const { data: historyData, isLoading: isJobHistoryLoading } =
    useGetCaseImporterJobHistoryQuery(
      {
        limit: itemsPerPage,
        offset: (currentPage - 1) * itemsPerPage,
      },
      {
        refetchInterval: 5000,
      },
    );

  const { mutate: stopCaseImporterJob } = useStopCaseImporterJobMutation({
    onSuccess: () => {
      setStopImportLoading(false);
      setStopImportModalOpen(false);
    },
    onError: () => {
      setStopImportLoading(false);
      handleErrorToast({
        toastMessage: 'Oops!',
        toastDescription:
          'We encountered a problem while stopping the import. Please try again.',
      });
    },
    onMutate: () => {
      setStopImportLoading(true);
    },
  });

  const activeJob = activeJobData?.getCaseImporterJobProgress;
  const hasActiveJob =
    activeJob &&
    (activeJob.status === 'PENDING' ||
      activeJob.status === 'IN_PROGRESS' ||
      activeJob.status === 'CANCELLING');
  const activeJobLoading = isJobProgressLoading && !hasActiveJob;

  const historyJobs = historyData?.getCaseImporterJobHistory?.jobs ?? [];
  const totalJobs: number = historyData?.getCaseImporterJobHistory?.total || 0;
  const hasHistory = historyJobs.length > 0;

  const selectedJob = selectedJobId
    ? historyJobs.find((job) => job.id === selectedJobId)
    : null;

  useEffect(() => {
    if (
      location.state?.fromNavbar &&
      segmentEnabled &&
      organisationId &&
      userId
    ) {
      trackCaseImportHistoryViewedEvent({
        organisation_id: Number(organisationId),
        user_id: String(userId),
        user_system_role: userSystemRole,
        has_active_import: !!hasActiveJob,
      });
    }
  }, [
    location.state?.fromNavbar,
    segmentEnabled,
    organisationId,
    userId,
    userSystemRole,
    hasActiveJob,
  ]);

  const handleCloseReport = () => {
    setIsReportOpen(false);
    setSelectedJobId(null);
  };

  const handleOpenReport = (
    job: CaseImporterJobHistoryItem,
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.stopPropagation();
    event.preventDefault();
    setSelectedJobId(job.id);
    setIsReportOpen(true);

    if (segmentEnabled && organisationId && userId && job) {
      trackCaseImportHistoryReportOpenedEvent({
        organisation_id: Number(organisationId),
        user_id: String(userId),
        job_id: job.id,
        file_name: job.importFileName,
        import_status: job.status,
      });
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (option: {
    value: number;
    label: string;
  }) => {
    setItemsPerPage(option.value);
    setCurrentPage(1);
  };

  const itemsPerPageOptions = [
    { value: 10, label: '10' },
    { value: 25, label: '25' },
    { value: 50, label: '50' },
    { value: 100, label: '100' },
  ];

  const columns: ColumnType[] = [
    { label: 'File name', value: 'fileName' },
    { label: 'Import ID', value: 'importId' },
    { label: 'Import date', value: 'importDate' },
    { label: 'Status', value: 'status' },
    { label: 'Cases', value: 'cases' },
    { label: 'Imported by', value: 'importedBy' },
    { label: 'Actions', value: 'actions' },
  ];

  const tableData = historyJobs.map((job) => {
    const statusBadgeStatus = getStatusBadgeStatus(
      job.status,
      job.successfulCases,
      job.failedCases,
      job.totalCases,
    );

    const statusCell = <StatusBadge status={statusBadgeStatus} />;

    let casesCell: React.ReactNode;
    if (statusBadgeStatus !== 'COMPLETED') {
      casesCell = (
        <StatusDetailsContainer>
          <StatusDetailLine $color="darkGrey">
            {job.failedCases > 0 && <>{job.failedCases} failed</>}{' '}
            {job.successfulCases > 0 && <> • {job.successfulCases} succeeded</>}
          </StatusDetailLine>
          <StatusDetailLine $color="lightGrey">
            of {job.totalCases} total
          </StatusDetailLine>
        </StatusDetailsContainer>
      );
    } else {
      casesCell = job.successfulCases;
    }

    return {
      row: job.id,
      cellData: [
        job.importFileName,
        getReadableId(job.id),
        formatImportDate(job.createdAt),
        statusCell,
        casesCell,
        job.createdByName ?? '-',
        <Button
          key={`action-${job.id}`}
          variant="tertiary"
          noPadding
          noMinWidth
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
            handleOpenReport(job, e);
          }}
          data-testid={`open-report-${job.id}`}
        >
          <StyledIcon className="uil uil-file-alt" size="16px" />
          Open Report
        </Button>,
      ],
    };
  });

  const numPages = Math.ceil(totalJobs / itemsPerPage);
  const startItem = totalJobs === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalJobs);
  const showingText = `Showing ${startItem} to ${endItem} of ${totalJobs} cases`;

  const selectedJobStatusBadge = selectedJob
    ? getStatusBadgeStatus(
        selectedJob.status,
        selectedJob.successfulCases,
        selectedJob.failedCases,
        selectedJob.totalCases,
      )
    : null;

  const tableLoading = isJobHistoryLoading && !historyData && !activeJobLoading;
  const showEmptyState =
    !hasActiveJob && !hasHistory && !tableLoading && !activeJobLoading;

  const confirmStopImportModal = () => {
    return (
      <Modal
        size="small"
        title="Stop Import"
        confirmButtonText="Stop Import"
        cancelButtonText="Keep importing"
        open={stopImportModalOpen}
        setOpen={setStopImportModalOpen}
        onCancel={() => setStopImportModalOpen(false)}
        onConfirm={() => {
          stopCaseImporterJob({ jobId: activeJob?.id || '' });
        }}
        loading={stopImportLoading}
      >
        <ModalDescription>
          You are about to stop the import from the{' '}
          <span>{activeJob?.fileName}</span> file.
        </ModalDescription>
        <ModalDescription>
          All cases that have already been imported will remain in the system.
          Any remaining cases in the file will not be imported.
        </ModalDescription>
        <ModalDescription>
          This action is irreversible. Once you stop this import, it cannot be
          resumed automatically. If you need to import the remaining data later,
          you will have to start a new import using the file again.
        </ModalDescription>
      </Modal>
    );
  };

  if (redirectUrl) {
    return <Redirect to="/uploadCases" />;
  }

  if (tableLoading)
    return (
      <Center>
        <Spinner variant="secondary" size="xlarge" />
      </Center>
    );

  return (
    <UploadHistoryContainer>
      <Top data-testid="upload-cases-history-header">
        <h2>Import History</h2>
        <span>View your case import history</span>
      </Top>
      {hasActiveJob && (
        <Container data-testid="upload-cases-history-progress">
          <ProgressCard
            title={
              activeJob.status === 'CANCELLING'
                ? 'Stopping Import'
                : 'Import in Progress'
            }
            description={
              activeJob.status === 'CANCELLING'
                ? 'Stopping the import — cases already imported will be kept.'
                : `Importing ${activeJob.totalCases} case(s) from your CSV file`
            }
            percentage={activeJob.percentComplete}
            onStopProgress={
              activeJob.status === 'CANCELLING'
                ? undefined
                : () => setStopImportModalOpen(true)
            }
            stopProgressLabel="Stop Import"
            progressInfo={
              <>
                {activeJob.successfulCases} of {activeJob.totalCases} cases
                imported
                {activeJob.failedCases > 0 && (
                  <>
                    {' | '}
                    <FailedCasesText>
                      <StyledIcon
                        className="uil uil-exclamation-triangle"
                        size="16px"
                      />
                      {activeJob.failedCases} case(s) failed to import
                    </FailedCasesText>
                  </>
                )}
              </>
            }
          />
        </Container>
      )}
      {activeJobLoading && <Spinner variant="secondary" size="small" />}
      {showEmptyState ? (
        <Container data-testid="upload-cases-history-empty">
          <EmptyState
            data-testid="upload-cases-history"
            size="default"
            buttonText="Start Importing Cases"
            title="No Import Yet"
            description="Your import history will appear here after you import your first cases"
            onButtonClick={() => setRedirectUrl('/uploadCases')}
          />
        </Container>
      ) : (
        <>
          <TableWrapper data-testid="upload-cases-history-table">
            <PaginatedTable
              key={`table-${itemsPerPage}-${currentPage}`}
              columns={columns}
              tableData={tableData}
              visibleColumns={columns}
              numPages={numPages}
              currentPage={currentPage}
              handleClickPage={handlePageChange}
              isAlreadyPaginated={true}
              showPagination={false}
              emptyText="No import history available"
              data-testid="upload-cases-history-table"
              isFullWidth={true}
              borderRadius="12px"
              paginationPadding="0px"
              rowHeight={72}
              fixedRowHeight={true}
              minHeight={undefined}
            />
          </TableWrapper>
          <FooterContainer>
            <RightContainer>
              <DropdownSelect
                options={itemsPerPageOptions}
                value={itemsPerPageOptions.find(
                  (opt) => opt.value === itemsPerPage,
                )}
                onChange={(option) =>
                  handleItemsPerPageChange(
                    option as { value: number; label: string },
                  )
                }
                width="80px"
                menuPlacement="bottom"
                data-testid="items-per-page-dropdown"
              />
              <ShowingText>{showingText}</ShowingText>
            </RightContainer>
            <PaginationContainer>
              <Pagination
                numPages={numPages}
                handleClickPage={handlePageChange}
                currentPageNumber={currentPage}
                data-testid="upload-cases-history-pagination"
              />
            </PaginationContainer>
          </FooterContainer>
        </>
      )}
      <SlideOut
        isOpen={isReportOpen && !!selectedJob}
        title="Import Report"
        onClose={handleCloseReport}
        onConfirm={handleCloseReport}
        confirmButtonText="Close"
        confirmButtonDataTestid="close-import-report"
        applyButtonDisabled={false}
        showFooter={false}
        containerDataTestid="import-report-slideout"
      >
        {selectedJob && (
          <ImportReportContent>
            <ImportReportHeader>
              <h3>Import Status</h3>
              {selectedJobStatusBadge && (
                <StatusBadge status={selectedJobStatusBadge} />
              )}
            </ImportReportHeader>
            <div>
              <ImportReportMetricsContainer>
                {selectedJob.successfulCases > 0 && (
                  <ImportReportMetricCard>
                    <HeroMetric
                      title="Imported Successfully"
                      metrics={[
                        {
                          title: 'New cases',
                          value: selectedJob.successfulCases,
                        },
                      ]}
                      dashboard="organisational"
                      icon="uil uil-check-circle"
                      data-testid="import-report-successful"
                    />
                  </ImportReportMetricCard>
                )}
                {selectedJob.failedCases > 0 && (
                  <ImportReportMetricCard>
                    <HeroMetric
                      title="Failed to Import"
                      metrics={[
                        {
                          title: 'Excluded from import',
                          value: selectedJob.failedCases,
                        },
                      ]}
                      dashboard="organisational"
                      icon="uil uil-exclamation-triangle"
                      data-testid="import-report-failed"
                    />
                  </ImportReportMetricCard>
                )}
              </ImportReportMetricsContainer>
              <ImportReportDividerWrapper>
                <Divider variant="horizontal" width="100%" />
              </ImportReportDividerWrapper>
              <ImportReportDetails jobId={selectedJob.id} />
            </div>
          </ImportReportContent>
        )}
      </SlideOut>
      {confirmStopImportModal()}
    </UploadHistoryContainer>
  );
};
